import type { HolidayTableSection } from '@/src/types/holidays'

export interface StayOption {
    /** The occupancy, exactly as the column is labelled, e.g. "Cost per person for single occupancy". */
    label: string
    /** The price cell, e.g. "AED 12,599". */
    price: string
}

const HAS_NUMBER = /\d/

/**
 * Turns the single-row pricing table into one option per occupancy, or `null` when the table is
 * not that shape — a multi-row table means something other than a price list, and it keeps its
 * table. Cells and column labels are placed, never reworded.
 *
 * The first column is usually a row header ("Category" over a cell reading "Price"), which is a
 * label for the row rather than an option. It is dropped only when its cell carries no number, so
 * a table that genuinely starts with a price keeps it.
 */
export function parseStayOptions(section: HolidayTableSection): StayOption[] | null {
    if (section.rows.length !== 1 || section.columns.length < 2) return null

    const row = section.rows[0]
    const first = (row[section.columns[0].key] ?? '').trim()
    const columns = HAS_NUMBER.test(first) ? section.columns : section.columns.slice(1)

    const options: StayOption[] = []

    for (const column of columns) {
        const price = (row[column.key] ?? '').trim()
        const label = column.label.trim()
        // A missing price or an unlabelled column would render a card that says nothing; the whole
        // table falls back rather than showing a gap where a price should be.
        if (!price || !label) return null
        options.push({ label, price })
    }

    return options.length ? options : null
}
